import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Delete, EmojiEvents } from '@material-ui/icons';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Box, Flex } from 'UIKit/Grid';
import {
  fetchOlimpicRanking,
  REQUEST_GET_OLIMPIC_RANKING,
  fetchAddParticipation,
  fetchResults,
  REQUEST_GET_OLIMPIC_RESULTS,
  fetchClearParticipant,
} from '@/modules/Olimpic';

import OlimpicTable from 'sama_global/components/OlimpicTable';
import { useSnackbarListener } from 'sama_global/utils/hooks/snackbar';
import { useLoadbar } from 'sama_global/utils/hooks/loadbar';
import ToolButton from 'sama_global/components/Table/ToolButton';
import Form from 'sama_global/components/Form';
import Modal from 'sama_global/components/Modal';
import useOpen from 'sama_global/utils/hooks/open';
import { isAdmin } from 'sama_global/utils/protect';
import { downloadExcel } from 'sama_global/pages/olimpic/ranking/helpers';
import { fetchSchools, REQUEST_SCHOOLS } from '@/modules/User';
import Spinner from 'sama_global/components/Spinner/Spinner';
import StyledButton from 'sama_global/components/StyledButton';

const Rating = ({
  requests,
  olimpic,
  user,
  edit,
  intl,
  getRating,
  getResults,
  clearResult,
  loadDelegates,
}) => {
  const id = olimpic?._id;

  useEffect(() => {
    if (id) {
      getRating(id);
    }
  }, [id]);

  const request = requests[REQUEST_GET_OLIMPIC_RANKING];
  const resultsRequest = requests[REQUEST_GET_OLIMPIC_RESULTS];
  const schoolsRequest = requests[REQUEST_SCHOOLS];

  const delegates = useMemo(
    () => schoolsRequest?.data?.filter(delegate => delegate.meta?.smgl),
    [schoolsRequest?.data]
  );

  useSnackbarListener(request?.errorMessage);
  useLoadbar(REQUEST_GET_OLIMPIC_RANKING);

  const { opened, open, close } = useOpen(false);
  const [selected, setSelected] = useState([]);

  const [isLoading, setIsLoading] = useState(false);

  const handleDelete = () => {
    open('delete');
  };

  const handleSubmitDelete = async () => {
    setIsLoading(true);
    await Promise.all(selected.map(i => clearResult(request.data[i]?._id)));
    setIsLoading(false);
    close();
    getRating(id);
    setSelected([]);
  };

  const handleSubmit = async formData => {
    await edit(
      {
        ids: selected.map(i => request.data[i]?.userId),
        params: formData,
      },
      id
    );

    close();
    getRating(id);
    setSelected([]);
  };

  const allowDownload = () => {
    if (isAdmin({ user })) return downloadProgress ? 'disabled' : true;
    return false;
  };

  const options = useMemo(
    () =>
      isAdmin({ user })
        ? {
            selectableRows: 'multiple',
            customToolbarSelect: () => (
              <Flex pr={7}>
                <ToolButton title="SET_PLACE" onClick={() => open('place')}>
                  <EmojiEvents />
                </ToolButton>
                <ToolButton title="CLEAR_RESULT" onClick={handleDelete}>
                  <Delete />
                </ToolButton>
              </Flex>
            ),
            onRowClick: (d, { dataIndex }) => {
              setSelected([dataIndex]);
              open('place');
            },
          }
        : {},
    [user, selected]
  );

  const downloadProgress = resultsRequest?.loading || schoolsRequest?.loading;

  useEffect(() => {
    if (downloadProgress) return;
    if (!resultsRequest?.data || !schoolsRequest?.data) return;
    downloadExcel(
      resultsRequest.data.map(item => {
        const delegate = delegates.find(d => d.id === item.user.creator_id);
        return {
          ...item,
          delegate: delegate?.name_en + delegate?.surname_en || '',
        };
      }),
      intl
    );
  }, [resultsRequest, schoolsRequest, downloadProgress]);

  return (
    <Box>
      <Modal open={opened === 'place'} onClose={close}>
        <Form
          title="SELECT_PLACE"
          onSubmit={handleSubmit}
          fields={[{ name: 'place', locale: 'PLACE' }]}
          form="olimpic_index"
        />
      </Modal>
      <Modal open={opened === 'delete'} onClose={close}>
        <h3>
          <FormattedMessage id="DELETE_CONFIRM" />
        </h3>

        <StyledButton isLoading={isLoading} onClick={handleSubmitDelete}>
          <FormattedMessage id="CONFIRM" />
        </StyledButton>
      </Modal>
      {request && (
        <OlimpicTable
          title="RESULTS"
          data={request.data}
          options={{
            ...options,
            onRowSelectionChange: (c, a, rows) => setSelected(rows),
            rowsSelected: selected,
            download: allowDownload(),
            onDownload: () => {
              getResults(id);
              loadDelegates();
              return false;
            },
          }}
          components={downloadProgress && { icons: { DownloadIcon: Spinner } }}
          result
        />
      )}
    </Box>
  );
};

Rating.propTypes = {
  getRating: PropTypes.func.isRequired,
  edit: PropTypes.func.isRequired,
  requests: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  olimpic: PropTypes.object.isRequired,
  intl: PropTypes.object.isRequired,
  getResults: PropTypes.func.isRequired,
  loadDelegates: PropTypes.func.isRequired,
  clearResult: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  user: state.user,
  requests: state.requests,
  olimpic: state.olimpic.sama,
});

const mapDispatchToProps = dispatch => ({
  getRating(id) {
    dispatch(fetchOlimpicRanking(id));
  },
  edit(formData, id) {
    return dispatch(fetchAddParticipation(formData, id));
  },
  getResults(id) {
    dispatch(fetchResults(id));
  },
  clearResult(id) {
    return dispatch(fetchClearParticipant(id));
  },
  loadDelegates() {
    dispatch(fetchSchools());
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(Rating));
